import React, { useState, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend
} from 'recharts';
import {
  X, TrendingUp, CheckCircle2, Clock, AlertTriangle,
  BarChart3, MapPin, Calendar, Users, Download, CalendarRange,
  FileSpreadsheet, FileText as FilePdf, Flame
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import api from '../lib/api';
import HeatmapTab from './HeatmapTab';

const STATUS_COLORS = {
  'Reparacion Finalizada': '#008040',
  'En Ejecucion': '#f97316',
  'Pendiente': '#9ca3af',
  'Rechazada Corto': '#ec4899',
  'Sin Estado': '#6b7280',
};
const CHART_COLORS = ['#008040', '#FFD700', '#3b82f6', '#f97316', '#ec4899', '#8b5cf6', '#06b6d4', '#84cc16'];

function KpiCard({ icon: Icon, label, value, sub, color = 'text-primary' }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-border p-4 flex items-center gap-4">
      <div className="p-3 rounded-xl bg-muted">
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <p className="text-2xl font-bold">{value ?? '—'}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function CustomTooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-900 border border-border rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
}

function legendFormatter(value) {
  return <span className="text-xs">{value}</span>;
}

function buildQueryParams(dateFrom, dateTo) {
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  return params.toString() ? `?${params.toString()}` : '';
}

const THIS_YEAR = new Date().getFullYear();
const FMT_DATE = d => new Date(d).toISOString().split('T')[0];
const QUICK_PRESETS = [
  { label: 'Este año', from: `${THIS_YEAR}-01-01`, to: '' },
  { label: 'Últ. 6 meses', from: FMT_DATE(Date.now() - 180 * 86400000), to: '' },
  { label: 'Últ. 3 meses', from: FMT_DATE(Date.now() - 90 * 86400000), to: '' },
  { label: 'Últ. mes', from: FMT_DATE(Date.now() - 30 * 86400000), to: '' },
];

export default function AnalyticsDashboard({ onClose }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const contentRef = useRef(null);

  const qparams = buildQueryParams(appliedFrom, appliedTo);

  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['analytics-summary', appliedFrom, appliedTo],
    queryFn: () => api.get(`/analytics/summary${qparams}`).then(r => r.data),
  });

  const { data: byMonth } = useQuery({
    queryKey: ['analytics-by-month', appliedFrom, appliedTo],
    queryFn: () => api.get(`/analytics/by-month?months=12${appliedFrom || appliedTo ? '&' + new URLSearchParams({ date_from: appliedFrom, date_to: appliedTo }).toString() : ''}`).then(r => r.data),
  });

  const { data: byBarrio } = useQuery({
    queryKey: ['analytics-by-barrio', appliedFrom, appliedTo],
    queryFn: () => api.get(`/analytics/by-barrio?limit=10${qparams ? '&' + qparams.slice(1) : ''}`).then(r => r.data),
  });

  const { data: byEncargado } = useQuery({
    queryKey: ['analytics-by-encargado', appliedFrom, appliedTo],
    queryFn: () => api.get(`/analytics/by-encargado?limit=12${qparams ? '&' + qparams.slice(1) : ''}`).then(r => r.data),
  });

  const { data: byStatus } = useQuery({
    queryKey: ['analytics-by-status', appliedFrom, appliedTo],
    queryFn: () => api.get(`/analytics/by-status${qparams}`).then(r => r.data),
  });

  const { data: byType } = useQuery({
    queryKey: ['analytics-by-type', appliedFrom, appliedTo],
    queryFn: () => api.get(`/analytics/by-type?limit=8${qparams ? '&' + qparams.slice(1) : ''}`).then(r => r.data),
  });

  const { data: heatmapData, isLoading: loadingHeatmap, error: heatmapError } = useQuery({
    queryKey: ['analytics-heatmap', appliedFrom, appliedTo],
    queryFn: () => api.get(`/analytics/heatmap${qparams}`).then(r => r.data),
    staleTime: 5 * 60 * 1000,
    enabled: false, // HeatmapTab manages its own query
  });

  const monthData = byMonth?.data || [];
  const barrioData = byBarrio?.data || [];
  const encargadoData = byEncargado?.data || [];
  const statusData = (byStatus?.data || []).slice(0, 8);
  const typeData = byType?.data || [];
  const heatPoints = heatmapData?.points || [];

  const applyFilters = useCallback(() => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  }, [dateFrom, dateTo]);

  const clearFilters = useCallback(() => {
    setDateFrom('');
    setDateTo('');
    setAppliedFrom('');
    setAppliedTo('');
  }, []);

  const hasFilter = appliedFrom || appliedTo;

  const exportToExcel = useCallback(() => {    const wb = XLSX.utils.book_new();
    const dateLabel = appliedFrom || appliedTo
      ? ` (${appliedFrom || 'inicio'} – ${appliedTo || 'hoy'})`
      : '';

    // Sheet 1: Summary KPIs
    if (summary) {
      const summaryRows = [
        ['Métrica', 'Valor'],
        ['Total Incidencias', summary.total],
        ['Abiertas', summary.open],
        ['Completadas', summary.completed],
        ['Tasa de Resolución', `${summary.resolution_rate}%`],
        ['Alta Prioridad Abiertas', summary.high_priority_open],
        ['Días Medios Resolución', summary.avg_resolution_days ?? 'N/A'],
        ['Período', dateLabel || 'Todos los datos'],
      ];
      const ws = XLSX.utils.aoa_to_sheet(summaryRows);
      ws['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Resumen');
    }

    // Sheet 2: Por Barrio
    if (barrioData.length > 0) {
      const rows = [['Barrio', 'Total', 'Abiertas', 'Resueltas', 'Tasa (%)']];
      barrioData.forEach(b => rows.push([b.barrio, b.count, b.open, b.completed, b.rate]));
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Por Barrio');
    }

    // Sheet 3: Por Encargado
    if (encargadoData.length > 0) {
      const rows = [['Encargado', 'Total', 'Abiertas', 'Resueltas', 'Tasa (%)']];
      encargadoData.forEach(e => rows.push([e.encargado, e.total, e.open, e.completed, e.rate]));
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 35 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Por Encargado');
    }

    // Sheet 4: Tendencias Mensuales
    if (monthData.length > 0) {
      const rows = [['Mes', 'Creadas', 'Resueltas']];
      monthData.forEach(m => rows.push([m.label, m.count, m.completed]));
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Tendencias');
    }

    // Sheet 5: Por Estado
    if (statusData.length > 0) {
      const rows = [['Estado', 'Total']];
      statusData.forEach(s => rows.push([s.status, s.count]));
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 30 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Por Estado');
    }

    const filename = `analytics_scmejora_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  }, [summary, barrioData, encargadoData, monthData, statusData, appliedFrom, appliedTo]);

  const exportToPDF = useCallback(async () => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const dateLabel = appliedFrom || appliedTo
        ? `Período: ${appliedFrom || 'inicio'} – ${appliedTo || 'hoy'}`
        : `Generado: ${new Date().toLocaleDateString('es-ES')}`;

      // Header bar
      doc.setFillColor(0, 128, 64);
      doc.rect(0, 0, 210, 22, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Analytics – Incidencias Santa Cruz', 14, 14);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(dateLabel, 14, 28);

      let y = 33;

      if (summary) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Resumen General', 14, y);
        autoTable(doc, {
          startY: y + 3,
          head: [['Métrica', 'Valor']],
          body: [
            ['Total Incidencias', (summary.total ?? 0).toLocaleString('es-ES')],
            ['Abiertas', (summary.open ?? 0).toLocaleString('es-ES')],
            ['Completadas', (summary.completed ?? 0).toLocaleString('es-ES')],
            ['Tasa de Resolución', `${summary.resolution_rate ?? 0}%`],
            ['Alta Prioridad Abiertas', (summary.high_priority_open ?? 0).toLocaleString('es-ES')],
          ],
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2.5 },
          headStyles: { fillColor: [0, 128, 64], textColor: 255, fontStyle: 'bold' },
          columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 40 } },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      if (statusData.length > 0) {
        if (y > 210) { doc.addPage(); y = 15; }
        doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
        doc.text('Distribución por Estado', 14, y);
        autoTable(doc, {
          startY: y + 3,
          head: [['Estado', 'Total']],
          body: statusData.map(s => [s.status, s.count]),
          theme: 'striped',
          styles: { fontSize: 8, cellPadding: 2.5 },
          headStyles: { fillColor: [0, 128, 64], textColor: 255, fontStyle: 'bold' },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      if (barrioData.length > 0) {
        if (y > 210) { doc.addPage(); y = 15; }
        doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
        doc.text('Top 10 Barrios / Zonas', 14, y);
        autoTable(doc, {
          startY: y + 3,
          head: [['Barrio', 'Total', 'Abiertas', 'Resueltas', 'Tasa']],
          body: barrioData.map(b => [b.barrio, b.count, b.open, b.completed, `${b.rate}%`]),
          theme: 'striped',
          styles: { fontSize: 7.5, cellPadding: 2 },
          headStyles: { fillColor: [0, 128, 64], textColor: 255, fontStyle: 'bold' },
          columnStyles: { 0: { cellWidth: 70 } },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      if (encargadoData.length > 0) {
        if (y > 210) { doc.addPage(); y = 15; }
        doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
        doc.text('Rendimiento por Encargado', 14, y);
        autoTable(doc, {
          startY: y + 3,
          head: [['Encargado', 'Total', 'Abiertas', 'Resueltas', 'Tasa']],
          body: encargadoData.map(e => [e.encargado, e.total, e.open, e.completed, `${e.rate}%`]),
          theme: 'striped',
          styles: { fontSize: 7.5, cellPadding: 2 },
          headStyles: { fillColor: [0, 128, 64], textColor: 255, fontStyle: 'bold' },
          columnStyles: { 0: { cellWidth: 70 } },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      if (monthData.length > 0) {
        if (y > 210) { doc.addPage(); y = 15; }
        doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
        doc.text('Tendencias Mensuales', 14, y);
        autoTable(doc, {
          startY: y + 3,
          head: [['Mes', 'Creadas', 'Resueltas']],
          body: monthData.map(m => [m.label, m.count, m.completed]),
          theme: 'striped',
          styles: { fontSize: 8, cellPadding: 2.5 },
          headStyles: { fillColor: [0, 128, 64], textColor: 255, fontStyle: 'bold' },
        });
      }

      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(`Página ${i} / ${pageCount}`, 14, 290);
        doc.text('Valoriza Servicios Medioambientales', 196, 290, { align: 'right' });
      }

      doc.save(`analytics_scmejora_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
    }
  }, [summary, barrioData, encargadoData, monthData, statusData, appliedFrom, appliedTo]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-gray-50 dark:bg-gray-950 rounded-2xl shadow-2xl w-full max-w-5xl mx-4 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-white dark:bg-gray-900 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-lg">
              <BarChart3 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Analytics de Incidencias</h2>
              <p className="text-xs text-muted-foreground">
                Santa Cruz de Tenerife
                {hasFilter && (
                  <span className="ml-2 text-primary font-medium">
                    · Filtrado: {appliedFrom || 'inicio'} – {appliedTo || 'hoy'}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Export Buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={exportToPDF}
              data-testid="analytics-export-pdf"
              className="h-8 text-xs"
            >
              <FilePdf className="w-3.5 h-3.5 mr-1.5 text-red-600" />
              Exportar PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              data-testid="analytics-export"
              className="h-8 text-xs"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-green-600" />
              Exportar Excel
            </Button>
            <button
              onClick={onClose}
              data-testid="analytics-close"
              className="p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Date Filter Bar */}
        <div className="px-6 py-3 bg-white dark:bg-gray-900 border-b border-border shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <CalendarRange className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-muted-foreground">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  data-testid="analytics-date-from"
                  className="text-xs border border-border rounded-md px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-muted-foreground">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  data-testid="analytics-date-to"
                  className="text-xs border border-border rounded-md px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <Button size="sm" onClick={applyFilters} data-testid="analytics-apply-filter" className="h-7 text-xs">
                Aplicar
              </Button>
              {hasFilter && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="analytics-clear-filter" className="h-7 text-xs text-muted-foreground">
                  Limpiar filtros
                </Button>
              )}
            </div>
            {/* Quick presets */}
            <div className="flex gap-1 ml-auto flex-wrap">
              {QUICK_PRESETS.map(preset => {
                const isActive = appliedFrom === preset.from && appliedTo === preset.to;
                const handleClick = () => { setDateFrom(preset.from); setDateTo(preset.to); setAppliedFrom(preset.from); setAppliedTo(preset.to); };
                return (
                  <button
                    key={preset.label}
                    onClick={handleClick}
                    className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" ref={contentRef}>
          <Tabs defaultValue="resumen">
            <TabsList className="mb-6 bg-white dark:bg-gray-900 border border-border">
              <TabsTrigger value="resumen" data-testid="tab-resumen">
                <TrendingUp className="w-4 h-4 mr-1.5" /> Resumen
              </TabsTrigger>
              <TabsTrigger value="tendencias" data-testid="tab-tendencias">
                <Calendar className="w-4 h-4 mr-1.5" /> Tendencias
              </TabsTrigger>
              <TabsTrigger value="zonas" data-testid="tab-zonas">
                <MapPin className="w-4 h-4 mr-1.5" /> Por Zona
              </TabsTrigger>
              <TabsTrigger value="equipo" data-testid="tab-equipo">
                <Users className="w-4 h-4 mr-1.5" /> Equipo
              </TabsTrigger>
              <TabsTrigger value="heatmap" data-testid="tab-heatmap">
                <Flame className="w-4 h-4 mr-1.5" /> Mapa de Calor
              </TabsTrigger>
            </TabsList>

            {/* === TAB RESUMEN === */}
            <TabsContent value="resumen" className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard icon={TrendingUp} label="Total Incidencias" value={summary?.total?.toLocaleString('es-ES')} color="text-blue-600" />
                <KpiCard icon={Clock} label="Abiertas" value={summary?.open?.toLocaleString('es-ES')} color="text-orange-500" />
                <KpiCard
                  icon={CheckCircle2}
                  label="Resueltas"
                  value={summary?.completed?.toLocaleString('es-ES')}
                  sub={`${summary?.resolution_rate ?? 0}% tasa de resolución`}
                  color="text-green-600"
                />
                <KpiCard icon={AlertTriangle} label="Alta Prioridad" value={summary?.high_priority_open?.toLocaleString('es-ES')} color="text-red-600" />
              </div>

              {/* Status distribution */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-border p-5">
                <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" /> Distribución por Estado
                </h3>
                {statusData.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={statusData} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={55} outerRadius={90}>
                          {statusData.map((entry, i) => (
                            <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltipContent />} />
                        <Legend formatter={legendFormatter} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {statusData.map((s, i) => {
                        const total = statusData.reduce((a, b) => a + b.count, 0);
                        const pct = total ? Math.round(s.count / total * 100) : 0;
                        return (
                          <div key={s.status} className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[s.status] || CHART_COLORS[i % CHART_COLORS.length] }} />
                            <span className="text-xs flex-1 truncate">{s.status}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: STATUS_COLORS[s.status] || CHART_COLORS[i % CHART_COLORS.length] }} />
                              </div>
                              <span className="text-xs text-muted-foreground w-8 text-right">{s.count}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">Cargando datos...</p>
                )}
              </div>

              {typeData.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-border p-5">
                  <h3 className="font-semibold text-sm mb-4">Tipos de Incidencia Más Frecuentes</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={typeData} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="tipo" type="category" width={140} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltipContent />} />
                      <Bar dataKey="count" fill="#008040" name="Total" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </TabsContent>

            {/* === TAB TENDENCIAS === */}
            <TabsContent value="tendencias">
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-border p-5">
                <h3 className="font-semibold text-sm mb-1">Incidencias por Mes (últimos 12 meses)</h3>
                <p className="text-xs text-muted-foreground mb-4">Total creadas y resueltas cada mes</p>
                {monthData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltipContent />} />
                      <Legend formatter={legendFormatter} />
                      <Line type="monotone" dataKey="count" name="Creadas" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      <Line type="monotone" dataKey="completed" name="Resueltas" stroke="#008040" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">Cargando tendencias...</div>
                )}
              </div>
            </TabsContent>

            {/* === TAB ZONAS === */}
            <TabsContent value="zonas">
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-border p-5">
                <h3 className="font-semibold text-sm mb-4">Top 10 Barrios por Incidencias</h3>
                {barrioData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={barrioData} layout="vertical" margin={{ left: 10, right: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="barrio" type="category" width={130} tick={{ fontSize: 10 }} />
                        <Tooltip content={<CustomTooltipContent />} />
                        <Bar dataKey="open" name="Abiertas" stackId="a" fill="#f97316" />
                        <Bar dataKey="completed" name="Resueltas" stackId="a" fill="#008040" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-border text-muted-foreground">
                            <th className="text-left py-2 pr-3 font-medium">Barrio</th>
                            <th className="text-right py-2 px-3 font-medium">Total</th>
                            <th className="text-right py-2 px-3 font-medium">Abiertas</th>
                            <th className="text-right py-2 px-3 font-medium">Resueltas</th>
                            <th className="text-right py-2 pl-3 font-medium">Tasa</th>
                          </tr>
                        </thead>
                        <tbody>
                          {barrioData.map(b => (
                            <tr key={b.barrio} className="border-b border-border/50 hover:bg-muted/30">
                              <td className="py-2 pr-3 font-medium">{b.barrio}</td>
                              <td className="text-right py-2 px-3">{b.count}</td>
                              <td className="text-right py-2 px-3 text-orange-600">{b.open}</td>
                              <td className="text-right py-2 px-3 text-green-600">{b.completed}</td>
                              <td className="text-right py-2 pl-3">
                                <span className={`font-semibold ${b.rate >= 70 ? 'text-green-600' : b.rate >= 40 ? 'text-orange-500' : 'text-red-600'}`}>{b.rate}%</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">Cargando datos por zona...</div>
                )}
              </div>
            </TabsContent>

            {/* === TAB EQUIPO === */}
            <TabsContent value="equipo">
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-border p-5">
                <h3 className="font-semibold text-sm mb-4">Rendimiento por Encargado</h3>
                {encargadoData.length > 0 ? (
                  <>
                    <div className="space-y-3">
                      {encargadoData.map((e, i) => (
                        <div key={e.encargado} className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium truncate">{e.encargado}</span>
                              <div className="flex items-center gap-3 ml-2 flex-shrink-0">
                                <span className="text-xs text-muted-foreground">{e.total} total</span>
                                <span className={`text-xs font-bold ${e.rate >= 70 ? 'text-green-600' : e.rate >= 40 ? 'text-orange-500' : 'text-red-500'}`}>{e.rate}%</span>
                              </div>
                            </div>
                            <div className="flex gap-1 h-2">
                              <div className="bg-[#008040] rounded-l-full" style={{ width: e.total ? `${(e.completed / e.total) * 100}%` : '0%' }} />
                              <div className="bg-orange-400 rounded-r-full" style={{ width: e.total ? `${(e.open / e.total) * 100}%` : '0%' }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5"><div className="w-3 h-2 bg-[#008040] rounded" /> Resueltas</div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-2 bg-orange-400 rounded" /> Abiertas</div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">Cargando datos del equipo...</div>
                )}
              </div>
            </TabsContent>

            {/* === TAB MAPA DE CALOR === */}
            <TabsContent value="heatmap" className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-border p-5">
                <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                  <Flame className="w-4 h-4 text-orange-500" />
                  Densidad de Incidencias — Santa Cruz de Tenerife
                  {heatPoints.length > 0 && (
                    <span className="ml-auto text-xs text-muted-foreground font-normal">
                      {heatPoints.length.toLocaleString('es-ES')} puntos cargados
                    </span>
                  )}
                </h3>
                <HeatmapTab
                  appliedFrom={appliedFrom}
                  appliedTo={appliedTo}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
